import { supabase } from '../lib/supabase';
import type { Database } from '../lib/database.types';

type CourseRow = Database['public']['Tables']['courses']['Row'];

export interface Course {
  id: string;
  name: string;
  location: string;
  lat: number;
  lon: number;
  rating: number;
  reviews: number;
  pricePerPlayer: number;
  bookingFeePercent: number;
  distance: string;
  images: string[];
  holes: number;
  availability: string;
  description: string;
  amenities: string[];
  refundPolicy: string;
  refundPolicyDescription: string;
  refundPolicyId: string | null;
  refundPolicyHours: number | null;
  reviewBreakdown: {
    5: number;
    4: number;
    3: number;
    2: number;
    1: number;
  };
  topAspects: {
    positive: string[];
    negative: string[];
  };
}

const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1725724767938-26e57f67a12c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxkaXNjJTIwZ29sZiUyMGNvdXJzZXxlbnwxfHx8fDE3NjA3Mzk4NjZ8MA&ixlib=rb-4.1.0&q=80&w=1080';

interface RefundPolicy {
  name: string;
  refund_percent: number;
  window_hours: number;
}

function buildPolicyDescription(policy: RefundPolicy | null | undefined): string {
  if (!policy) return '';
  const hours = policy.window_hours;
  if (hours === 0) return 'Full refund for any cancellation before your tee time. No-shows receive no refund.';
  return `Full refund if cancelled ${hours}+ hours before your tee time. No refund within ${hours} hours.`;
}

async function fetchPoliciesMap(): Promise<Map<string, RefundPolicy>> {
  const { data } = await supabase
    .from('refund_policies')
    .select('id, name, refund_percent, window_hours');
  const map = new Map<string, RefundPolicy>();
  for (const p of data ?? []) map.set(p.id, p);
  return map;
}

interface ReviewAggregate {
  rating: number;
  reviews: number;
  reviewBreakdown: { 5: number; 4: number; 3: number; 2: number; 1: number };
  topAspects: { positive: string[]; negative: string[] };
}

function transformCourse(dbCourse: CourseRow & { refund_policy_id?: string | null }, policy: RefundPolicy | null | undefined, pricePerPlayer?: number, reviewData?: ReviewAggregate): Course {
  const amenities: string[] = [];
  if (dbCourse.amenity_restrooms) amenities.push('Restrooms');
  if (dbCourse.amenity_pro_shop) amenities.push('Pro Shop');
  if (dbCourse.amenity_food_drinks) amenities.push('Food & Drinks');
  if (dbCourse.amenity_equipment_rental) amenities.push('Equipment Rental');
  if (dbCourse.amenity_practice_area) amenities.push('Practice Area');
  if (dbCourse.amenity_water_stations) amenities.push('Water Stations');
  if (dbCourse.amenity_picnic_area) amenities.push('Picnic Area');
  if (dbCourse.amenity_dog_friendly) amenities.push('Dog Friendly');
  if (dbCourse.amenity_garbage_cans) amenities.push('Garbage Cans');

  const toPublicUrl = (path: string) =>
    supabase.storage.from('course-photos').getPublicUrl(path).data.publicUrl;

  const images: string[] = [];
  if (dbCourse.photo_main_path) {
    images.push(toPublicUrl(dbCourse.photo_main_path));
  }
  if (dbCourse.photo_gallery_paths && dbCourse.photo_gallery_paths.length > 0) {
    images.push(...dbCourse.photo_gallery_paths.map(toPublicUrl));
  }
  if (images.length === 0) {
    images.push(DEFAULT_IMAGE);
  }

  const lat = dbCourse.latitude || 45.5152;
  const lon = dbCourse.longitude || -122.6784;

  return {
    id: dbCourse.id,
    name: dbCourse.name,
    location: dbCourse.location || dbCourse.address || 'Unknown Location',
    lat,
    lon,
    rating: reviewData?.rating ?? 0,
    reviews: reviewData?.reviews ?? 0,
    pricePerPlayer: pricePerPlayer || 20,
    bookingFeePercent: dbCourse.booking_fee_percent ?? 5,
    distance: '—',
    images,
    holes: dbCourse.holes,
    availability: dbCourse.status === 'active' ? 'Available Today' : 'Check Availability',
    description: dbCourse.description || 'A great disc golf course.',
    amenities,
    refundPolicy: policy?.name ?? 'No policy set',
    refundPolicyDescription: buildPolicyDescription(policy),
    refundPolicyId: (dbCourse as any).refund_policy_id ?? null,
    refundPolicyHours: policy?.window_hours ?? null,
    reviewBreakdown: reviewData?.reviewBreakdown ?? { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
    topAspects: reviewData?.topAspects ?? { positive: [], negative: [] },
  };
}

function buildReviewAggregates(reviews: { course_id: string; rating: number; positive_aspects: string[]; negative_aspects: string[] }[]): Map<string, ReviewAggregate> {
  const map = new Map<string, ReviewAggregate>();
  for (const r of reviews) {
    if (!map.has(r.course_id)) {
      map.set(r.course_id, { rating: 0, reviews: 0, reviewBreakdown: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }, topAspects: { positive: [], negative: [] } });
    }
    const agg = map.get(r.course_id)!;
    agg.reviews += 1;
    agg.rating += r.rating;
    const star = r.rating as 1 | 2 | 3 | 4 | 5;
    if (star >= 1 && star <= 5) agg.reviewBreakdown[star] += 1;
    for (const a of (r.positive_aspects || [])) {
      if (!agg.topAspects.positive.includes(a)) agg.topAspects.positive.push(a);
    }
    for (const a of (r.negative_aspects || [])) {
      if (!agg.topAspects.negative.includes(a)) agg.topAspects.negative.push(a);
    }
  }
  // Compute average rating
  for (const [, agg] of map) {
    agg.rating = agg.reviews > 0 ? Math.round(agg.rating / agg.reviews * 10) / 10 : 0;
  }
  return map;
}

export async function fetchCourses(): Promise<Course[]> {
  const [{ data: courses, error }, policiesMap] = await Promise.all([
    supabase.from('courses').select('*').eq('status', 'active').is('deleted_at', null),
    fetchPoliciesMap(),
  ]);

  if (error) {
    console.error('Error fetching courses:', error);
    return [];
  }

  if (!courses || courses.length === 0) return [];

  const courseIds = courses.map((c: any) => c.id);
  const [coursesWithPricing, { data: allReviews }] = await Promise.all([
    Promise.all(
      courses.map(async (course: any) => {
        const { data: configs } = await supabase
          .from('tee_time_configs')
          .select('price_cents, pricing_type')
          .eq('course_id', course.id)
          .eq('status', 'active')
          .is('deleted_at', null)
          .limit(1);
        return { course, configs };
      })
    ),
    supabase
      .from('reviews')
      .select('course_id, rating, positive_aspects, negative_aspects')
      .in('course_id', courseIds),
  ]);

  const reviewsMap = buildReviewAggregates(allReviews || []);

  return coursesWithPricing.map(({ course, configs }: any) => {
    const config = configs?.[0] as { price_cents: number; pricing_type: string } | undefined;
    const pricePerPlayer = config?.price_cents ? config.price_cents / 100 : undefined;
    const policy = course.refund_policy_id ? policiesMap.get(course.refund_policy_id) : null;
    return transformCourse(course, policy, pricePerPlayer, reviewsMap.get(course.id));
  });
}

export async function fetchCourseById(courseId: string): Promise<Course | null> {
  const [{ data: course, error }, policiesMap] = await Promise.all([
    supabase.from('courses').select('*').eq('id', courseId).single(),
    fetchPoliciesMap(),
  ]);

  if (error || !course) {
    console.error('Error fetching course:', error);
    return null;
  }

  const [{ data: configs }, { data: courseReviews }] = await Promise.all([
    supabase
      .from('tee_time_configs')
      .select('price_cents, pricing_type')
      .eq('course_id', courseId)
      .eq('status', 'active')
      .is('deleted_at', null)
      .limit(1),
    supabase
      .from('reviews')
      .select('course_id, rating, positive_aspects, negative_aspects')
      .eq('course_id', courseId),
  ]);

  const config = configs?.[0] as { price_cents: number; pricing_type: string } | undefined;
  const pricePerPlayer = config?.price_cents ? config.price_cents / 100 : undefined;
  const policy = (course as any).refund_policy_id ? policiesMap.get((course as any).refund_policy_id) : null;
  const reviewsMap = buildReviewAggregates(courseReviews || []);

  return transformCourse(course, policy, pricePerPlayer, reviewsMap.get(courseId));
}
